import { useCallback } from 'react'
import { UserFileRead } from '@/uppy-file-upload/schemas'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { FileOverflowMenu } from './FileOverflowMenu'
import { DriveListItem } from '@/user_files/drive/components/drive-list-item/DriveListItem'
import { DriveListItemStatus } from '@/user_files/drive/constants'
import { fileProcessingStatusToDriveStatus, formatDriveListUpdatedAtParts, formatFileSize } from '@/user_files/utils/drive-list-format'
import { useViewUserFile } from '@/user_files/files/hooks/useViewUserFile'
import { downloadFile } from '@/user_files/files/fetch/download-user-file'
import { useAppDispatch } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs, kToastDurationShortMs } from '@/constants/constants-ui'
import { UserFileTypeIcon } from './UserFileTypeIcon'

interface FileListItemProps {
  file: UserFileRead
  disabled?: boolean
  selected: boolean
  onToggleSelect: () => void
  onRename?: () => void
  onDelete: () => void
  onMove?: () => void
  onRetry?: () => void
}

/**
 * FileListItem
 *
 * Single file row in the drive list. Uses shared DriveListItem.
 */
export function FileListItem({ file, disabled, selected, onToggleSelect, onRename, onDelete, onMove, onRetry }: FileListItemProps) {
  const dispatch = useAppDispatch()
  const viewUserFile = useViewUserFile()
  const status = fileProcessingStatusToDriveStatus(file.processing_status)
  const isProcessing = status === DriveListItemStatus.Processing || status === DriveListItemStatus.Pending
  const errorMessage = status === DriveListItemStatus.Failed ? file.processing_error ?? 'Processing Failed' : undefined
  const { date, time } = formatDriveListUpdatedAtParts(file.updated_at)
  const isReady = file.processing_status === UserFileProcessingStatus.Completed

  const handleDownload = useCallback(async () => {
    try {
      await downloadFile({
        fileId: file.id,
        storagePath: file.storage_path,
        fileName: file.original_filename,
      })
      dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: `${file.original_filename} downloaded successfully`,
          durationMs: kToastDurationShortMs,
        })
      )
    } catch {
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: `${file.original_filename} download failed`,
          durationMs: kToastDurationMediumMs,
        })
      )
    }
  }, [dispatch, file.id, file.original_filename, file.storage_path])

  return (
    <DriveListItem
      variant="file"
      status={status}
      name={file.original_filename}
      formattedDate={date}
      formattedTime={time}
      detailsLabel={file.size_bytes != null ? formatFileSize(file.size_bytes) : undefined}
      errorMessage={errorMessage}
      disabled={disabled}
      selected={selected}
      onToggleSelect={onToggleSelect}
      onClick={isReady ? () => viewUserFile(file) : undefined}
      onRetry={status === DriveListItemStatus.Failed ? onRetry : undefined}
      fileIcon={<UserFileTypeIcon detectedContentType={file.detected_content_type} className="w-6 h-6 shrink-0 text-brand-neutral-950" />}
      overflowMenu={<FileOverflowMenu onDownload={handleDownload} onRename={onRename} onDelete={onDelete} onMove={onMove} disabled={isProcessing} />}
    />
  )
}
